import { captureException } from "@sentry/react";
import { isAxiosError } from "axios";

import { omgApiClient } from "@lib/api/omgApiClient";

// Bound the probe so a black-holed host resolves quickly to "unreachable"
// rather than hanging the canary. Shorter than the 30s checkout POST timeout —
// this fires before card entry to catch the blocked-network class up front, so
// it should not sit for the full charge budget.
export const CHECKOUT_API_PROBE_TIMEOUT_MS = 8_000;

// An ECONNABORTED that lands this far under the budget was not the timer
// firing; it was the request being torn down (page navigation, reload, the
// component unmounting). Measured on prod (OMG-2589): every real timeout came
// in at 8,005 to 8,164 ms, every cancellation at 120 to 484 ms. 90% of the
// budget separates the two with room on both sides.
//
// The tradeoff this accepts, stated so nobody reads the split as exact: a
// request that legitimately runs between the floor (7,200 ms) and the 8,000 ms
// axios timeout is recorded as a timeout rather than as the near miss it is.
// The prod sample that set the floor was small (n=7 cancellations), so the two
// clusters could sit closer together than they did in that window. Accepted: a
// checkout API that takes over 7 seconds to answer is unusable for a buyer
// anyway, so "timeout" is the right verdict for that request either way.
export const CHECKOUT_API_PROBE_TIMEOUT_FLOOR_MS = Math.round(
  CHECKOUT_API_PROBE_TIMEOUT_MS * 0.9,
);

// One retry before a verdict, and ONLY for `no-response`: a single mount-time
// ping is exposed to a transient blip on a flaky mobile connection, and that
// retry is near free (the failure came back immediately). A `timeout` is not
// retried, because the first attempt already spent the whole 8s budget and a
// second one would double the wall time the buyer waits for a verdict we can
// already report. A `cancelled` attempt is not retried either; there is nobody
// left to tell.
export const CHECKOUT_API_PROBE_MAX_ATTEMPTS = 2;

/**
 * What the probe concluded (OMG-2589). These are the three things the old
 * single "unreachable" verdict conflated, measured over 30 days of prod:
 *
 * - `no-response`: the host never answered and the failure was not a timeout
 *   (axios ERR_NETWORK, or any code we do not recognise). DNS black-hole,
 *   filtered network, AV web-shield, dead connection. The OMG-2065 class this
 *   probe exists for. Reported at ERROR.
 * - `timeout`: the host did not answer within the budget. On prod this was 61%
 *   of flags after the OMG-2539 host change, almost all iOS Safari and in-app
 *   browsers on slow connections, and more than a quarter of those buyers
 *   still purchased within the day (OMG-2382 read: 28% of flagged buyers,
 *   against a 46% baseline conversion for the same funnel step). So it is a
 *   risk signal, not a hard block. Reported at WARNING.
 * - `cancelled`: the request was aborted before any verdict (unmount via the
 *   AbortSignal, or a navigation tearing it down, seen as an ECONNABORTED far
 *   under the budget). Not a network verdict at all. Not reported.
 */
export const CHECKOUT_API_PROBE_OUTCOMES = [
  "reachable",
  "no-response",
  "timeout",
  "cancelled",
] as const;
export type CheckoutApiProbeOutcome =
  (typeof CHECKOUT_API_PROBE_OUTCOMES)[number];

export interface CheckoutApiProbeResult {
  /** true when the host ANSWERED (any HTTP status), on any attempt. */
  reachable: boolean;
  outcome: CheckoutApiProbeOutcome;
  /** The omg-api host that was probed (for the analytics dimension). */
  host?: string;
  /** axios error code of the LAST failed attempt (e.g. ECONNABORTED, ERR_NETWORK). */
  code?: string;
  /** Wall time across every attempt. */
  elapsedMs: number;
  attempts: number;
}

const apiHost = (): string | undefined => {
  try {
    return new URL(import.meta.env.VITE_OMG_API_URL as string).host;
  } catch {
    return undefined;
  }
};

type FailedAttempt = {
  outcome: Exclude<CheckoutApiProbeOutcome, "reachable">;
  code?: string;
};

/**
 * Classify one response-less failure. Cancellation is recognised two ways:
 * axios's own ERR_CANCELED (our AbortSignal fired) and an ECONNABORTED that
 * arrived far under the budget (the browser tore the request down on a
 * navigation, which axios also surfaces as ECONNABORTED). Everything else
 * without a response is the host not answering.
 */
export const classifyProbeFailure = (
  error: unknown,
  attemptElapsedMs: number,
): FailedAttempt => {
  const code = isAxiosError(error) ? error.code : undefined;
  if (code === "ERR_CANCELED") return { outcome: "cancelled", code };
  if (code === "ECONNABORTED") {
    return attemptElapsedMs >= CHECKOUT_API_PROBE_TIMEOUT_FLOOR_MS
      ? { outcome: "timeout", code }
      : { outcome: "cancelled", code };
  }
  return { outcome: "no-response", code };
};

// OMG-2134: probe the omg-api host the checkout POST will use with a lightweight
// GET /customers/ping. Reachability is defined by whether the host ANSWERED:
// any HTTP response (even a non-2xx) means the network path works, so only a
// response-less failure counts against it. That is the invisible class behind
// the OMG-2065 silent spinners.
//
// History, so nobody re-derives it: this probe measured privacy blockers
// black-holing the raw onrender.com host (OMG-2066/OMG-2382). OMG-2539 moved the
// app to the first-party api.omgyes.com and the black-hole signature (fast
// ERR_NETWORK reload storms) disappeared, but the counter did not fall to zero,
// because the old single verdict also counted slow networks and cancelled
// requests. OMG-2589 split it into the three outcomes above.
//
// Captures a WRAPPED Sentry error on a reportable failure so the distinct
// message survives the global ignoreErrors network filter (utils/sentry.ts),
// matching the OMG-2130 checkout-catch pattern. The message carries the
// OUTCOME, so `no-response` and `timeout` group as separate Sentry issues.
// Never throws — the caller fires the analytics event off the returned result.
export const probeCheckoutApi = async (
  signal?: AbortSignal,
): Promise<CheckoutApiProbeResult> => {
  const startedAt = performance.now();
  const host = apiHost();

  let answered = false;
  let last: FailedAttempt | undefined;
  let attempts = 0;

  while (attempts < CHECKOUT_API_PROBE_MAX_ATTEMPTS) {
    attempts += 1;
    const attemptStartedAt = performance.now();
    try {
      await omgApiClient.get("/customers/ping", {
        timeout: CHECKOUT_API_PROBE_TIMEOUT_MS,
        signal,
      });
      answered = true;
      break;
    } catch (error) {
      // A response with any status means the host is reachable — the path
      // works, the endpoint just answered non-2xx. Not an unreachable event.
      if (isAxiosError(error) && error.response) {
        answered = true;
        break;
      }
      last = classifyProbeFailure(
        error,
        Math.round(performance.now() - attemptStartedAt),
      );
      // Our own AbortSignal firing between attempts counts as a cancellation
      // too, whatever the in-flight request happened to report.
      if (last.outcome === "cancelled" || signal?.aborted) {
        last = { outcome: "cancelled", code: last.code };
        break;
      }
      // Only a host that never answered earns the retry (see MAX_ATTEMPTS).
      if (last.outcome !== "no-response") break;
    }
  }

  // Read the clock ONCE, here, so every terminal path below reports the same
  // total — including the Sentry extra and the returned result.
  const totalMs = Math.round(performance.now() - startedAt);

  if (answered) {
    return {
      reachable: true,
      outcome: "reachable",
      host,
      elapsedMs: totalMs,
      attempts,
    };
  }

  // The loop cannot leave `last` unset while MAX_ATTEMPTS is >= 1, but fall
  // back rather than assert: a probe must never throw on the checkout path.
  const failure: FailedAttempt = last ?? { outcome: "no-response" };

  if (failure.outcome === "cancelled") {
    return {
      reachable: false,
      outcome: "cancelled",
      host,
      code: failure.code,
      elapsedMs: totalMs,
      attempts,
    };
  }

  captureException(
    new Error(`CheckoutApiUnreachableError: ${failure.outcome}`),
    {
      level: failure.outcome === "timeout" ? "warning" : "error",
      tags: {
        area: "checkout",
        phase: "api-reachability",
        outcome: failure.outcome,
      },
      extra: { host, axiosCode: failure.code, attempts, elapsedMs: totalMs },
    },
  );
  return {
    reachable: false,
    outcome: failure.outcome,
    host,
    code: failure.code,
    elapsedMs: totalMs,
    attempts,
  };
};
